/**
 * Pie/Donut Chart Widget (Story 5.5 → Epic 37 redesign on shadcn/ui charts)
 *
 * Rendered with shadcn's ChartContainer (Recharts v3). ChartContainer enforces a
 * height so the donut actually renders (the old height:100% in a flex parent
 * measured 0 → only the legend showed).
 *
 * SIZE-AWARE: the widget measures its own height (ResizeObserver) and degrades so
 * it never overflows. In a SHORT box (the scaled sidebar-preview frame, ~108px)
 * it renders just a clean mini-ring — no center total, no legend, title hidden —
 * which is why the preview previously turned to mush (donut + legend + center
 * label all crammed + overlapping). With room, it shows the navy donut + center
 * total + legend. Navy monochrome palette per design direction. Anim off (PDF).
 */

import { PieChart, Pie, Cell, Label } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "../../ui/chart";
import type { WidgetConfig } from "../../../lib/templateUtils";
import type { DistributionData, WidgetDataEnvelope } from "../../../lib/widgetData";
import { isDistributionData } from "../../../lib/widgetData";
import { useChartWidgetShell, ChartMessage, NAVY_SERIES, compactNumber } from "./chartWidgetShell";

const NAVY_SLICE_COLORS = NAVY_SERIES;
const formatTotal = compactNumber;

interface PieChartWidgetProps {
  widget: WidgetConfig;
  data?: WidgetDataEnvelope<DistributionData>;
}

export function PieChartWidget({ widget, data }: PieChartWidgetProps) {
  const config = widget.config as { metricKey?: string; groupBy?: string; maxSlices?: number };
  const colSpan = widget.position.colSpan;
  const { compact, shell } = useChartWidgetShell(widget.config);

  if (data?.loading) {
    return shell("pie-chart", widget.title, <div className="h-20 w-20 animate-pulse rounded-full bg-muted" />, { centered: true });
  }
  if (data?.error) {
    return shell("pie-chart", widget.title, <ChartMessage role="alert" testId="pie-chart-error">{data.error}</ChartMessage>, { centered: true });
  }

  const chartData = data?.data;
  if (!chartData || !isDistributionData(chartData) || chartData.slices.length === 0) {
    return shell("pie-chart", widget.title, <ChartMessage testId="pie-chart-no-data">No data available</ChartMessage>, { centered: true });
  }
  if (colSpan <= 2 && !compact) {
    return shell("pie-chart", widget.title, <ChartMessage testId="chart-placeholder">Widen widget to view chart</ChartMessage>, { centered: true });
  }

  // Cap to a tidy set; roll the tail into "Other".
  let slices = chartData.slices;
  const effectiveMaxSlices = config.maxSlices ? Math.max(config.maxSlices, 2) : undefined;
  if (effectiveMaxSlices && slices.length > effectiveMaxSlices) {
    const top = slices.slice(0, effectiveMaxSlices - 1);
    const otherValue = slices.slice(effectiveMaxSlices - 1).reduce((sum, s) => sum + s.value, 0);
    slices = [...top, { name: "Other", value: otherValue }];
  }

  const total = slices.reduce((sum, s) => sum + s.value, 0);
  const sideLayout = !compact && colSpan >= 7;

  const chartConfig: ChartConfig = {};
  const pieData = slices.map((s, i) => {
    const color = NAVY_SLICE_COLORS[i % NAVY_SLICE_COLORS.length];
    chartConfig[s.name] = { label: s.name, color };
    return { ...s, fill: color };
  });

  const legend = (
    <ul
      className={`flex min-w-0 ${sideLayout ? "max-w-[42%] flex-col justify-center gap-1.5" : "mt-2 flex-row flex-wrap justify-center gap-x-3 gap-y-1"}`}
      data-testid="pie-legend"
    >
      {pieData.map((s, i) => {
        const pct = total > 0 ? ((s.value / total) * 100).toFixed(1) : "0.0";
        return (
          <li key={`${s.name}-${i}`} className="flex min-w-0 items-center gap-1.5 text-[11px]">
            <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ backgroundColor: s.fill }} aria-hidden />
            <span className="truncate text-foreground">{s.name}</span>
            <span className="shrink-0 tabular-nums text-muted-foreground">{pct}%</span>
          </li>
        );
      })}
    </ul>
  );

  const chart = (
    <div className="flex min-h-0 flex-1 items-center justify-center">
      <ChartContainer
        config={chartConfig}
        className={`aspect-square w-full ${compact ? "max-h-[88px] max-w-[88px]" : "max-h-[170px] max-w-[170px]"}`}
        data-testid="pie-chart-container"
      >
        <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
          <ChartTooltip content={<ChartTooltipContent hideLabel nameKey="name" />} />
          <Pie
            data={pieData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius="100%"
            innerRadius="64%"
            paddingAngle={1.5}
            stroke="hsl(var(--background))"
            strokeWidth={2}
            isAnimationActive={false}
          >
            {pieData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
            {!compact && (
              <Label
                content={({ viewBox }) => {
                  if (!viewBox || !("cx" in viewBox)) return null;
                  const { cx, cy } = viewBox as { cx: number; cy: number };
                  return (
                    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle">
                      <tspan x={cx} y={cy - 2} className="fill-foreground" style={{ fontSize: 18, fontWeight: 600 }}>
                        {formatTotal(total)}
                      </tspan>
                      <tspan x={cx} y={cy + 14} className="fill-muted-foreground" style={{ fontSize: 10 }}>
                        Total
                      </tspan>
                    </text>
                  );
                }}
              />
            )}
          </Pie>
        </PieChart>
      </ChartContainer>
    </div>
  );

  return shell(
    "pie-chart",
    widget.title,
    <div className={`flex min-h-0 flex-1 ${sideLayout ? "flex-row items-center gap-3" : "flex-col"}`}>
      {chart}
      {!compact && pieData.length > 1 && legend}
    </div>,
  );
}
